import { API_URL } from "../constants";
import { authHeader } from "./auth";

// Lightweight product analytics via the existing Google Tag Manager container
// (initialized in index.js). Events are pushed to the dataLayer; wire them to
// GA4 (or anything else) in the GTM UI — no code changes needed per tool.
//
// Event vocabulary (all snake_case, prefixed by flow):
//   prep_prepare_clicked                 dashboard "Prepare a listing" CTA
//   prep_property_opened   {status}      a property card opened
//   personalize_opened     {sections_total}
//   personalize_section_approved  {section_type}
//   personalize_section_edited    {section_type}   first hand-edit per section
//   personalize_section_rewritten {section_type, preset}
//   personalize_save_conflict
//   personalize_save_failed {status}
//   personalize_section_reset {section_type, sections_total}
//   personalize_completed  {seconds_active, sections_total, sections_approved,
//                           sections_edited, pct_edited}   ← the brief's success
//                           metrics: review time + share of sections touched
//   share_opened           {was_stale}
//   share_download         {format}      pdf | pptx | deck | one_pager
//   share_native_share     {format}
//   share_public_link_created
//   share_snapshot         {variant, method, count?}
//   share_social_copied    {label}       (superseded by social_caption_copied)
//   social_content_opened                (retired with the separate social
//                          content page; captions and snapshots now render on
//                          Share, whose open is already share_opened)
//   social_caption_copied  {label}
//   credits_spent          {action, cost, balance}   a debit went through
//   credits_insufficient   {action, cost, balance}   the 409 paywall sheet opened
//   credits_pack_purchased {pack, credits}           /success after a pack
//   credits_plan_purchased {plan_key}                /subscription-success
//   credits_exhausted_view {action}                  the plan rows were expanded
//
// Every name here must also be on the server's allowlist in views.py — the
// endpoint drops unlisted events with a 400 that this module deliberately
// swallows, so the only thing that catches the mismatch is the contract test
// in betaContracts.test.js. Add new events to both places.
//
// Report editor/share events also carry computation_uuid and document_version
// as top-level first-party context, never as free-form properties.

// The credits vocabulary and the property keys each event carries — the
// server allowlists BOTH the name and the keys (extra keys drop the whole
// event), so every emitter and the contract test read from this one map.
export const CREDITS_EVENTS = Object.freeze({
  credits_spent: ["action", "cost", "balance"],
  credits_insufficient: ["action", "cost", "balance"],
  credits_pack_purchased: ["pack", "credits"],
  credits_plan_purchased: ["plan_key"],
  credits_exhausted_view: ["action"],
});

/**
 * Push a product event to the GTM dataLayer. Analytics must never break the
 * app: any failure (no window, blocked script, weird prop) is swallowed.
 *
 * @param {string} event
 * @param {Record<string, string | number | boolean>} [props]
 */
export const track = (event, props = {}) => {
  try {
    const analyticsWindow = /** @type {Window & { dataLayer?: Record<string, any>[] }} */ (window);
    analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
    analyticsWindow.dataLayer.push({ event, ...props });
  } catch {
    /* never let analytics affect the product */
  }

  // Dual-send to the first-party, allowlisted event endpoint. Keep only
  // bounded primitives; object values and long/user-entered strings are
  // intentionally excluded so analytics never becomes a PII side channel.
  try {
    /** @type {Record<string, string | number | boolean>} */
    const properties = {};
    /** @type {Record<string, any>} */
    const body = { event_name: event, properties };
    for (const [key, value] of Object.entries(props)) {
      if (["listing_uuid", "computation_uuid", "document_version"].includes(key)) {
        body[key] = value;
      } else if (
        typeof value === "boolean" ||
        typeof value === "number" ||
        typeof value === "string"
      ) {
        // The server rejects the WHOLE event if any string property exceeds
        // 64 chars — clamp here so a long label degrades to a truncated
        // property instead of silently dropping the first-party event while
        // GTM still records it (skewed metrics, no visible error).
        properties[key] = typeof value === "string" ? value.slice(0, 64) : value;
      }
    }
    const headers = new Headers({ "Content-Type": "application/json" });
    for (const [key, value] of Object.entries(authHeader())) headers.set(key, value);
    fetch(`${API_URL}/events/`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* analytics is always best-effort */
  }
};
